import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StoryGallery, { StoryPhoto } from './StoryGallery';
import { legacyMilestones } from '../data/legacyMilestones';
import NarrationControls from './NarrationControls';
import LegacyTimelineSidebar from './LegacyTimelineSidebar';
import { useLegacyTimelineViewModel } from '../hooks/useLegacyTimelineViewModel';
import { useNarrationPlayer } from '../hooks/useNarrationPlayer';
import { useSlideshowController } from '../hooks/useSlideshowController';
import { useWakeLock } from '../hooks/useWakeLock';
import { useSectionVisibilityGuard } from '../hooks/useSectionVisibilityGuard';
import { useNarrationToggle } from '../hooks/useNarrationToggle';
import { useNarrationAdvance } from '../hooks/useNarrationAdvance';
import { useNarrationDurations } from '../hooks/useNarrationDurations';
import { analyticsService } from '../services/api';

const LegacyTimeline: React.FC = () => {
  const [isTouring, setIsTouring] = useState(false);
  const [narrationEnabled, setNarrationEnabled] = useState(false);
  const [currentPhoto, setCurrentPhoto] = useState<StoryPhoto | null>(() => {
    const initialPhotos = (legacyMilestones[0]?.photos as StoryPhoto[]) ?? [];
    return initialPhotos[0] ?? null;
  });
  const [isMuted, setIsMuted] = useState(false);
  const [yearIntroSignal, setYearIntroSignal] = useState(0);
  const [resetSignal, setResetSignal] = useState(0);
  const [narrationStartPending, setNarrationStartPending] = useState(false);
  const [skipCancel, setSkipCancel] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [lastSpokenKey, setLastSpokenKey] = useState<string | null>(null);
  const [firstSequenceKickEnabled, setFirstSequenceKickEnabled] = useState(false);
  const tourLockRef = useRef(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const fadeOutTimeoutRef = useRef<number | null>(null);
  const fadeOutStartRef = useRef<number | null>(null);
  const fadeOutDurationMs = 1200;
  const firstSequenceKickTimerRef = useRef<number | null>(null);
  const narrationEnabledRef = useRef(false);
  const activeIndexRef = useRef(0);
  const activePhotoIndexRef = useRef(0);
  const mutedRef = useRef(false);
  const presentationPlayStartRef = useRef<number | null>(null);
  const lastPlayingStateRef = useRef(false);
  const {
    activeIndex,
    activePhotoIndex,
    requestedPhotoIndex,
    advanceSignal,
    activeMilestone,
    activePhotos,
    setActiveIndex,
    setActivePhotoIndex,
    setRequestedPhotoIndex,
    advancePhoto,
  } = useSlideshowController(legacyMilestones);
  const {
    audioRef,
    isSupported: isNarrationSupported,
    playSequence: playAudioSequence,
    stop: stopAudio,
    unlock: unlockAudio,
    playback,
  } = useNarrationPlayer(mutedRef);
  const narrationYear = activeMilestone.year;
  const getIntroAudioSrc = useCallback((year: string) => `/media/narration/${year}/intro.mp3`, []);
  const getPhotoAudioSrc = useCallback(
    (year: string, index: number) =>
      `/media/narration/${year}/${String(index + 1).padStart(2, '0')}.mp3`,
    []
  );
  const { totalSeconds, yearSources, bySrc, expectedCount, loadedCount } = useNarrationDurations(
    legacyMilestones,
    getIntroAudioSrc,
    getPhotoAudioSrc
  );

  const recordPresentationEvent = useCallback(
    async (eventType: string, payload?: Record<string, unknown>, watchSeconds?: number) => {
      try {
        await analyticsService.trackEvent({
          event_name: 'presentation_event',
          page_type: 'presentation',
          page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
          page_title: typeof document !== 'undefined' ? document.title : undefined,
          payload: {
            event_type: eventType,
            watch_seconds: watchSeconds,
            ...payload,
          },
        });
      } catch (error) {
        // Swallow analytics errors to avoid impacting the presentation.
      }
    },
    []
  );

  useEffect(() => {
    void recordPresentationEvent('view', {
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });
  }, [recordPresentationEvent]);

  const fadeOutAndStopNarration = useCallback(() => {
    if (isFadingOut) return;
    setIsFadingOut(true);
    fadeOutStartRef.current = window.performance?.now?.() ?? Date.now();
    setSkipCancel(true);
    setNarrationEnabled(false);
    if (fadeOutTimeoutRef.current) {
      window.clearTimeout(fadeOutTimeoutRef.current);
    }
    fadeOutTimeoutRef.current = window.setTimeout(() => {
      stopAudio();
      setIsMuted(true);
      fadeOutTimeoutRef.current = null;
      fadeOutStartRef.current = null;
      setIsFadingOut(false);
    }, fadeOutDurationMs);
  }, [isFadingOut, stopAudio]);

  const speakPhoto = useCallback(
    (
      photo: StoryPhoto | null,
      options?: { immediate?: boolean; onComplete?: () => void; force?: boolean }
    ) => {
      if (!photo || (!options?.force && !narrationEnabled) || !isNarrationSupported) return;
      if (typeof window === 'undefined') {
        return;
      }
      const speakKey = `${activeMilestone.year}-${photo.url}`;
      if (!options?.force && speakKey === lastSpokenKey) {
        return;
      }
      setLastSpokenKey(speakKey);
      stopAudio();
      if (!narrationYear) return;
      const introSrc = activePhotoIndex === 0 ? getIntroAudioSrc(narrationYear) : null;
      const photoSrc = getPhotoAudioSrc(narrationYear, activePhotoIndex);
      const audioSources = [introSrc, photoSrc].filter(Boolean) as string[];
      if (audioSources.length === 0) return;
      playAudioSequence(audioSources, { onComplete: options?.onComplete });
      return;
    },
    [
      narrationEnabled,
      isNarrationSupported,
      stopAudio,
      getIntroAudioSrc,
      getPhotoAudioSrc,
      playAudioSequence,
      activePhotoIndex,
      activeMilestone,
      narrationYear,
      lastSpokenKey,
    ]
  );
  const goNext = useCallback(() => {
    setActiveIndex((prev) => {
      if (prev >= legacyMilestones.length - 1) {
        void recordPresentationEvent('complete', { year: legacyMilestones[prev]?.year });
        setNarrationEnabled(false);
        stopAudio();
        return prev;
      }
      return prev + 1;
    });
  }, [setActiveIndex, stopAudio, recordPresentationEvent]);
  const goPrevMilestone = useCallback(() => {
    setNarrationEnabled(false);
    stopAudio();
    setActiveIndex((prev) => Math.max(prev - 1, 0));
  }, [setActiveIndex, stopAudio]);
  const goNextMilestone = useCallback(() => {
    setNarrationEnabled(false);
    stopAudio();
    setActiveIndex((prev) => Math.min(prev + 1, legacyMilestones.length - 1));
  }, [setActiveIndex, stopAudio]);
  const { handleNarrationAdvance, handlePhotoChange } = useNarrationAdvance({
    narrationEnabled,
    activePhotos,
    activePhotoIndex,
    activeIndex,
    milestonesCount: legacyMilestones.length,
    activeMilestoneYear: activeMilestone.year,
    stopAudio,
    goNext,
    advancePhoto,
    setNarrationEnabled,
    setCurrentPhoto,
    setActivePhotoIndex,
  });

  const handlePhotoChangeWithAnalytics = useCallback(
    (photo: StoryPhoto, index: number, instanceId?: string) => {
      handlePhotoChange(photo, index, instanceId);
      const payload = {
        year: activeMilestone.year,
        photoIndex: index,
        photoUrl: photo?.url ?? null,
        instanceId: instanceId ?? null,
      };
      if ((narrationEnabled || isTouring) && presentationPlayStartRef.current !== null) {
        const now = window.performance?.now?.() ?? Date.now();
        const watchSeconds = Math.max((now - presentationPlayStartRef.current) / 1000, 0);
        presentationPlayStartRef.current = now;
        void recordPresentationEvent('slide_change', payload, watchSeconds);
        return;
      }
      void recordPresentationEvent('slide_change', payload);
    },
    [handlePhotoChange, activeMilestone.year, narrationEnabled, isTouring, recordPresentationEvent]
  );
  const handleCycleComplete = useCallback(() => {
    if (!isTouring || narrationEnabled || tourLockRef.current) return;
    tourLockRef.current = true;
    goNext();
    setTimeout(() => {
      tourLockRef.current = false;
    }, 200);
  }, [isTouring, narrationEnabled, goNext]);
  useEffect(() => {
    if (!isTouring) {
      tourLockRef.current = false;
    }
  }, [isTouring]);

  useEffect(() => {
    narrationEnabledRef.current = narrationEnabled;
  }, [narrationEnabled]);

  useEffect(() => {
    const isPlaying = narrationEnabled || isTouring;
    if (isPlaying === lastPlayingStateRef.current) return;
    lastPlayingStateRef.current = isPlaying;
    const now = window.performance?.now?.() ?? Date.now();
    if (isPlaying) {
      presentationPlayStartRef.current = now;
      void recordPresentationEvent('play', {
        year: activeMilestone.year,
        photoIndex: activePhotoIndex,
      });
      return;
    }
    const watchSeconds =
      presentationPlayStartRef.current !== null
        ? Math.max((now - presentationPlayStartRef.current) / 1000, 0)
        : undefined;
    presentationPlayStartRef.current = null;
    void recordPresentationEvent('pause', {
      year: activeMilestone.year,
      photoIndex: activePhotoIndex,
    }, watchSeconds);
  }, [narrationEnabled, isTouring, activeMilestone.year, activePhotoIndex, recordPresentationEvent]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
    activePhotoIndexRef.current = activePhotoIndex;
  }, [activeIndex, activePhotoIndex]);

  useEffect(() => {
    return () => {
      if (fadeOutTimeoutRef.current) {
        window.clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = null;
      }
      if (presentationPlayStartRef.current !== null) {
        const now = window.performance?.now?.() ?? Date.now();
        const watchSeconds = Math.max((now - presentationPlayStartRef.current) / 1000, 0);
        presentationPlayStartRef.current = null;
        void recordPresentationEvent('stop', { reason: 'unmount' }, watchSeconds);
      }
    };
  }, [recordPresentationEvent]);

  useWakeLock(narrationEnabled || isTouring);

  useEffect(() => {
    if (!narrationEnabled) {
      if (skipCancel) {
        setSkipCancel(false);
        return;
      }
      setLastSpokenKey(null);
      if (!isFadingOut) {
        stopAudio();
      }
      return;
    }
    if (narrationStartPending) {
      setNarrationStartPending(false);
      speakPhoto(currentPhoto, {
        immediate: true,
        force: true,
        onComplete: handleNarrationAdvance,
      });
      return;
    }
    speakPhoto(currentPhoto, {
      onComplete: handleNarrationAdvance,
    });
  }, [
    narrationEnabled,
    currentPhoto,
    speakPhoto,
    handleNarrationAdvance,
    stopAudio,
    narrationStartPending,
    skipCancel,
    isFadingOut,
  ]);

  useEffect(() => {
    mutedRef.current = isMuted;
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : 1;
    }
  }, [isMuted, audioRef]);

  useEffect(() => {
    setActivePhotoIndex(0);
    if (activePhotos.length > 0) {
      setCurrentPhoto(activePhotos[0]);
    } else {
      setCurrentPhoto(null);
    }
  }, [activePhotos, resetSignal, setActivePhotoIndex]);

  useEffect(() => {
    if (requestedPhotoIndex !== null && requestedPhotoIndex === activePhotoIndex) {
      setRequestedPhotoIndex(null);
    }
  }, [requestedPhotoIndex, activePhotoIndex, setRequestedPhotoIndex]);

  useEffect(() => {
    if (firstSequenceKickEnabled && (activeIndex !== 0 || activePhotoIndex !== 0)) {
      setFirstSequenceKickEnabled(false);
      if (firstSequenceKickTimerRef.current !== null) {
        window.clearTimeout(firstSequenceKickTimerRef.current);
        firstSequenceKickTimerRef.current = null;
      }
    }
  }, [activeIndex, activePhotoIndex, firstSequenceKickEnabled]);

  useEffect(() => {
    setYearIntroSignal((prev) => prev + 1);
  }, [activeIndex]);

  useSectionVisibilityGuard({
    enabled: narrationEnabled,
    sectionRef,
    onExit: fadeOutAndStopNarration,
  });

  const handleNarrationToggle = useNarrationToggle({
    isNarrationSupported,
    narrationEnabled,
    activePhotos,
    currentPhoto,
    unlockAudio,
    setNarrationStartPending,
    mutedRef,
    setIsMuted,
    fadeOutStartRef,
    setLastSpokenKey,
    setResetSignal,
    setRequestedPhotoIndex,
    setActivePhotoIndex,
    setCurrentPhoto,
    setFirstSequenceKickEnabled,
    setNarrationEnabled,
    firstSequenceKickTimerRef,
    narrationEnabledRef,
    activeIndexRef,
    activePhotoIndexRef,
    activeIndex,
    activePhotoIndex,
    handleNarrationAdvance,
  });

  const handleMuteToggle = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const nextMuted = !mutedRef.current;
      mutedRef.current = nextMuted;
      setIsMuted(nextMuted);
      if (!narrationEnabled) return;
      if (audioRef.current && audioRef.current.src) {
        audioRef.current.volume = nextMuted ? 0 : 1;
        return;
      }
      speakPhoto(currentPhoto, {
        immediate: true,
        force: true,
        onComplete: handleNarrationAdvance,
      });
    },
    [narrationEnabled, currentPhoto, speakPhoto, handleNarrationAdvance, audioRef]
  );

  const narrationControl = (
    <NarrationControls
      isSupported={isNarrationSupported}
      narrationEnabled={narrationEnabled}
      isMuted={isMuted}
      onToggle={handleNarrationToggle}
      onMute={handleMuteToggle}
    />
  );

  const {
    contextTitle,
    contextSubtitle,
    timelineYearsHeading,
    timelineYearsSubheading,
    instanceId,
    timelineYears,
  } = useLegacyTimelineViewModel(legacyMilestones, activeMilestone, activePhotoIndex);

  const handleSelectYear = useCallback(
    (year: string | number) => {
      const nextIndex = legacyMilestones.findIndex((milestone) => milestone.year === year);
      if (nextIndex === -1) return;
      tourLockRef.current = false;
      setIsTouring(false);
      setActiveIndex(nextIndex);
      setRequestedPhotoIndex(0);
    },
    [setActiveIndex, setRequestedPhotoIndex, setIsTouring]
  );

  const handleSidebarSelectYear = useCallback(
    (index: number) => {
      tourLockRef.current = false;
      setIsTouring(false);
      setActiveIndex(index);
    },
    [setActiveIndex, setIsTouring]
  );

  const handleSidebarSelectPhoto = useCallback(
    (yearIndex: number, photoIndex: number) => {
      if (yearIndex !== activeIndex) {
        setActiveIndex(yearIndex);
      }
      setRequestedPhotoIndex(photoIndex);
    },
    [activeIndex, setActiveIndex, setRequestedPhotoIndex]
  );

  const handleTimelinePhotoSelect = useCallback(
    (year: string | number, photoIndex: number) => {
      const nextIndex = legacyMilestones.findIndex((milestone) => milestone.year === year);
      if (nextIndex === -1) return;
      tourLockRef.current = false;
      setIsTouring(false);
      setActiveIndex(nextIndex);
      setRequestedPhotoIndex(photoIndex);
    },
    [setActiveIndex, setRequestedPhotoIndex, setIsTouring]
  );

  const timelineContent = (
    <LegacyTimelineSidebar
      milestones={legacyMilestones}
      activeIndex={activeIndex}
      activePhotoIndex={activePhotoIndex}
      onSelectYear={handleSidebarSelectYear}
      onSelectPhoto={handleSidebarSelectPhoto}
    />
  );

  const narrationTime = useMemo(() => {
    if (totalSeconds <= 0) {
      return { totalSeconds: 0, remainingSeconds: 0 };
    }
    let completed = 0;
    legacyMilestones.forEach((milestone, index) => {
      const sources = yearSources[milestone.year] ?? [];
      if (index < activeIndex) {
        completed += sources.reduce((acc, src) => acc + (bySrc[src] ?? 0), 0);
        return;
      }
      if (index > activeIndex) {
        return;
      }
      if (playback.src && sources.includes(playback.src)) {
        for (const src of sources) {
          if (src === playback.src) break;
          completed += bySrc[src] ?? 0;
        }
        completed += playback.currentTime;
        return;
      }
      const sliceEnd = Math.min(activePhotoIndex + 1, sources.length);
      completed += sources.slice(0, sliceEnd).reduce((acc, src) => acc + (bySrc[src] ?? 0), 0);
    });
    const remainingSeconds = Math.max(totalSeconds - completed, 0);
    return { totalSeconds, remainingSeconds };
  }, [
    totalSeconds,
    yearSources,
    bySrc,
    activeIndex,
    activePhotoIndex,
    playback,
  ]);

  return (
    <section
      id="legacy-timeline"
      ref={sectionRef}
      className="relative min-h-screen snap-start"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <StoryGallery
        photos={activePhotos}
        contextTitle={contextTitle}
        contextSubtitle={contextSubtitle}
        fullscreen
        showBottomCaption
        timeline={timelineContent}
        timelineYears={timelineYears}
        timelineYearsHeading={timelineYearsHeading}
        timelineYearsSubheading={timelineYearsSubheading}
        onTimelineYearSelect={handleSelectYear}
        compactPlayState={narrationEnabled}
        onCompactPlayToggle={handleNarrationToggle}
        kenBurnsActive={narrationEnabled || isTouring}
        externalPlayState={narrationEnabled ? false : isTouring}
        onPlayChange={(playing) => setIsTouring(playing)}
        onCycleComplete={handleCycleComplete}
        onPhotoChange={handlePhotoChangeWithAnalytics}
        advanceSignal={narrationEnabled ? advanceSignal : undefined}
        primaryControl={narrationControl}
        instanceId={instanceId}
        requestedPhotoIndex={requestedPhotoIndex}
        overallTotalSeconds={narrationTime.totalSeconds}
        overallRemainingSeconds={narrationTime.remainingSeconds}
        durationLoadedCount={loadedCount}
        durationExpectedCount={expectedCount}
        onTimelinePhotoSelect={handleTimelinePhotoSelect}
        sidebarRevealSignal={yearIntroSignal}
        onPrevGroup={goPrevMilestone}
        onNextGroup={goNextMilestone}
      />

      <div className="absolute inset-0 z-10 pointer-events-none">
      </div>
    </section>
  );
};

export default LegacyTimeline;
